import React, { useContext, useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Button, Box, Divider, Chip, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import API_BASE_URL from '../config';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SchoolIcon from '@mui/icons-material/School';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
// Remove react-beautiful-dnd import
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
// Add dnd-kit imports
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ResumeBuilder from './ResumeBuilder';

const serviceTiles = [
  {
    key: 'extracurricular-explorer',
    title: 'Extracurricular Explorer',
    subtitle: 'Service · AI-powered',
    description: 'Track unique extracurriculars tailored to you.',
    route: '/service/extracurricular',
    icon: <EmojiObjectsIcon sx={{ fontSize: 40, color: '#fff' }} />, // white for contrast
    gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    glow: '0 4px 32px 0 rgba(252,182,159,0.18)'
  },
  {
    key: 'academic-plan',
    title: 'Academic Plan',
    subtitle: 'Service · Smart Planner',
    description: 'Build your academic journey with smart course planning.',
    route: '/student-planning',
    icon: <SchoolIcon sx={{ fontSize: 36, color: '#667eea' }} />, // blue for contrast
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    glow: '0 4px 24px 0 rgba(168,237,234,0.18)'
  },
  {
    key: 'volunteer-match',
    title: 'Volunteer Match',
    subtitle: 'Service · Community',
    description: 'Match with volunteering opportunities for you.',
    route: '/service/volunteering',
    icon: <VolunteerActivismIcon sx={{ fontSize: 36, color: '#ff6b6b' }} />, // red for contrast
    gradient: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
    glow: '0 4px 24px 0 rgba(251,194,235,0.18)'
  }
];

// Mock AI suggestion function
function getSuggestedNextStep(ecCount, volunteerCount) {
  if (ecCount === 0) return 'Add your first extracurricular!';
  if (volunteerCount === 0) return 'Find a volunteering opportunity!';
  if (ecCount < 3) return 'Explore more activities to diversify your profile!';
  return 'Review your plan and set a new goal!';
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext) || {};
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(() => {
    const saved = localStorage.getItem('plan_completed_state');
    return saved ? JSON.parse(saved) : {};
  });
  const [aiNextStep, setAiNextStep] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Fetch plan on mount
  useEffect(() => {
    if (!user) return; // Only fetch if user is authenticated
    const fetchPlan = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/plan`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Failed to fetch plan');
        const data = await response.json();
        setPlan(data);
        // Only initialize completed state if nothing in localStorage
        const saved = localStorage.getItem('plan_completed_state');
        if (!saved) {
          const completedInit = {};
          data.forEach(item => { completedInit[item.id] = !!item.completed; });
          setCompleted(completedInit);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [user]);

  // Group plan items by type
  const [ecItems, setEcItems] = useState([]);
  const [volunteerItems, setVolunteerItems] = useState([]);
  useEffect(() => {
    setEcItems(plan.filter(item => item.item_type === 'ec'));
    setVolunteerItems(plan.filter(item => item.item_type === 'volunteer'));
  }, [plan]);

  // Remove item from plan
  const handleRemove = async (planItemId) => {
    // Optimistically update UI
    setPlan(plan => plan.filter(item => item.id !== planItemId));
    setCompleted(prev => { const c = { ...prev }; delete c[planItemId]; return c; });

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/plan/${planItemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.error || 'Failed to remove item');
        // Optionally, re-fetch the plan here if you want to restore the item
      }
    } catch (err) {
      setError('Failed to remove item');
      // Optionally, re-fetch the plan here if you want to restore the item
    }
  };

  const handleComplete = (planItemId) => {
    setCompleted(prev => {
      const updated = { ...prev, [planItemId]: !prev[planItemId] };
      localStorage.setItem('plan_completed_state', JSON.stringify(updated));
      return updated;
    });
  };

  // Helper SortableItem component for dnd-kit
  function SortableItem({ item, idx, completed, handleComplete, handleRemove, whiteCard }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
    return (
      <Card
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        sx={{
          bgcolor: whiteCard ? '#fff' : 'rgba(255,255,255,0.10)',
          color: whiteCard ? '#222' : '#fff',
          borderRadius: 3,
          mb: 2,
          boxShadow: isDragging ? 6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.7 : 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: whiteCard ? '#222' : '#fff' }}>{item.name}</Typography>
          <Typography variant="body2" sx={{ color: whiteCard ? '#555' : '#ffe082' }}>Time Commitment: {item.time_commitment || 'N/A'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant={completed[item.id] ? 'contained' : 'outlined'}
            color={completed[item.id] ? 'success' : 'primary'}
            onClick={() => handleComplete(item.id)}
            sx={{ minWidth: 120, fontWeight: 700, borderRadius: 2, background: completed[item.id] ? 'linear-gradient(90deg, #a8edea 0%, #fed6e3 100%)' : 'none', color: completed[item.id] ? '#222' : (whiteCard ? '#222' : '#fff'), borderColor: '#ffe082', '&:hover': { background: completed[item.id] ? 'linear-gradient(90deg, #fed6e3 0%, #a8edea 100%)' : 'rgba(255,255,255,0.15)', color: completed[item.id] ? '#fff' : '#ffe082' } }}
          >
            {completed[item.id] ? 'Completed' : 'Mark Completed'}
          </Button>
          <Button color="error" onClick={() => handleRemove(item.id)} sx={{ color: whiteCard ? '#d32f2f' : '#fff', borderColor: '#ffe082', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>Remove</Button>
        </Box>
      </Card>
    );
  }

  // Sensors for dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // dnd-kit drag end handlers
  const handleEcDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = ecItems.findIndex(item => item.id === active.id);
      const newIndex = ecItems.findIndex(item => item.id === over.id);
      const reordered = arrayMove(ecItems, oldIndex, newIndex);
      setEcItems(reordered);
      setPlan([...reordered, ...volunteerItems]);
    }
  };
  const handleVolunteerDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = volunteerItems.findIndex(item => item.id === active.id);
      const newIndex = volunteerItems.findIndex(item => item.id === over.id);
      const reordered = arrayMove(volunteerItems, oldIndex, newIndex);
      setVolunteerItems(reordered);
      setPlan([...ecItems, ...reordered]);
    }
  };

  // Get first name for greeting
  const firstName = user?.fullName?.split(' ')[0] || user?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'Pathly User';

  // Progress tracker for each section
  const ecTotal = ecItems.length;
  const ecCompleted = ecItems.filter(item => completed[item.id]).length;
  const ecProgress = ecTotal === 0 ? 0 : (ecCompleted / ecTotal) * 100;
  const volunteerTotal = volunteerItems.length;
  const volunteerCompleted = volunteerItems.filter(item => completed[item.id]).length;
  const volunteerProgress = volunteerTotal === 0 ? 0 : (volunteerCompleted / volunteerTotal) * 100;

  // AI-powered next step (mocked async, improved logic)
  useEffect(() => {
    setAiLoading(true);
    setTimeout(() => {
      if (plan.length === 0) {
        setAiNextStep('Add your first extracurricular or find a volunteering opportunity!');
      } else {
        const next = plan.find(item => !completed[item.id]);
        setAiNextStep(next ? `Finish: ${next.name}|${next.item_type}` : 'You are all caught up! 🎉');
      }
      setAiLoading(false);
    }, 800);
  }, [plan, completed]);

  // Next step button handler
  const handleNextStep = () => {
    if (aiNextStep.startsWith('Finish:')) {
      const [, nameType] = aiNextStep.split(': ');
      const [name, type] = nameType.split('|');
      if (type === 'ec') navigate('/service/extracurricular');
      else if (type === 'volunteer') navigate('/service/volunteering');
    } else if (aiNextStep.includes('extracurricular')) {
      navigate('/service/extracurricular');
    } else if (aiNextStep.includes('volunteer')) {
      navigate('/service/volunteering');
    }
  };

  const serviceCardSize = { width: 350, height: 370, minWidth: 350, maxWidth: 350, minHeight: 370, maxHeight: 370 };

  return (
    <Box sx={{ minHeight: '100dvh', height: '100dvh', width: '100%', overflowY: 'auto', overflowX: 'hidden', scrollSnapType: 'y mandatory', bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 0 }}>
      {/* Slide 1: Services */}
      <Box sx={{ minHeight: 'calc(100dvh)', height: 'calc(100dvh)', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', scrollSnapAlign: 'start', p: 0, m: 0, overflowX: 'hidden' }}>
        <Box sx={{ pt: { xs: 10, md: 12 }, mb: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h2" fontWeight={900} sx={{ mb: 1, color: '#fff', letterSpacing: -1, textAlign: 'center', fontSize: { xs: 28, md: 40 }, textShadow: '0 4px 24px #6e7eea88', lineHeight: 1.1 }}>
            Welcome, {firstName}!
          </Typography>
          <Typography variant="h5" sx={{ color: '#ffe082', fontWeight: 700, mb: 4, textAlign: 'center', fontSize: { xs: 16, md: 22 }, textShadow: '0 2px 8px #6e7eea55', lineHeight: 1.2 }}>
            🚀 Ready to level up your journey? Pathly's got your back. Pick a service below to get started! 🌈
          </Typography>
        </Box>
        <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', maxWidth: 1200, gap: 4, overflow: 'hidden' }}>
          {serviceTiles.map((tile, idx) => (
            <Card
              key={tile.key}
              sx={{ ...serviceCardSize, borderRadius: 6, boxShadow: tile.glow, background: tile.gradient, color: '#fff', p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', cursor: 'pointer', opacity: 0.97, overflow: 'hidden', '&:hover': { boxShadow: '0 12px 48px 0 rgba(110,126,234,0.18)', transform: 'scale(1.04)', opacity: 1 }, maxWidth: 350 }}
              onClick={() => navigate(tile.route)}
            >
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', flex: 1, overflow: 'hidden', justifyContent: 'space-between' }}>
                <Chip label={tile.subtitle} size="small" sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, letterSpacing: 1, fontSize: 14 }} />
                <Box sx={{ mb: 2 }}>{tile.icon}</Box>
                <Typography variant="h3" fontWeight={900} sx={{ mb: 1, letterSpacing: -1, fontSize: tile.key === 'extracurricular-explorer' ? 22 : 24, wordBreak: 'break-word', lineHeight: 1.1 }}>{tile.title}</Typography>
                <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.93)', fontSize: 14, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', width: '100%' }}>{tile.description}</Typography>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', mt: 'auto' }}>
                  <Button
                    variant="contained"
                    sx={{
                      background: 'rgba(255,255,255,0.18)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 18,
                      borderRadius: 2,
                      px: 5,
                      py: 1.7,
                      textTransform: 'none',
                      letterSpacing: 1.2,
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px #00000022',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.28)',
                        color: '#fff',
                        transform: 'scale(1.06)',
                        boxShadow: '0 8px 32px 0 rgba(110,126,234,0.25)'
                      }
                    }}
                    onClick={e => { e.stopPropagation(); navigate(tile.route); }}
                  >
                    {tile.key === 'extracurricular-explorer' ? 'Explore Activities' : tile.key === 'academic-plan' ? 'Get Activities' : 'Explore Opportunities'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
      {/* Slide 2: Pathly Journey (Plan) - always rendered after service cards */}
      <Box sx={{ minHeight: 'calc(100dvh)', height: 'calc(100dvh)', width: '100%', scrollSnapAlign: 'start', bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', px: { xs: 2, md: 8 }, py: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <Box sx={{ pt: { xs: 7, md: 8 }, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h3" fontWeight={900} gutterBottom sx={{ color: '#ffe082', mb: 2, mt: 0, textShadow: '0 2px 8px #6e7eea55', textAlign: 'center' }}>
            🌟 Your Pathly Journey
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, width: '100%', maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'row', gap: 4, justifyContent: 'center', alignItems: 'stretch', overflow: 'hidden' }}>
          {/* EC Plan Card */}
          <Card sx={{ flex: 1, minWidth: 0, maxWidth: 480, minHeight: 0, height: '100%', borderRadius: 6, boxShadow: '0 8px 32px 0 rgba(110,126,234,0.18)', background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', color: '#fff', p: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', overflow: 'hidden' }}>
            <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              <Typography variant="h5" fontWeight={900} sx={{ mb: 2, color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>🎨 Extracurriculars</Typography>
              <LinearProgress variant="determinate" value={ecProgress} sx={{ height: 12, borderRadius: 6, mb: 2, background: 'rgba(255,255,255,0.18)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ffe082 0%, #ff6b6b 100%)' } }} />
              <Typography variant="body2" sx={{ color: '#fff', mb: 2 }}>{ecCompleted}/{ecTotal} completed</Typography>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEcDragEnd}>
                <SortableContext items={ecItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
                  <Box sx={{ flex: 1, minHeight: 0, maxHeight: 420, overflowY: 'auto' }}>
                    {ecItems.length === 0 ? (
                      <Typography sx={{ color: '#fff', fontSize: 18, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>✨ No extracurriculars yet — add some! ✏️</Typography>
                    ) : (
                      ecItems.map((item, idx) => (
                        <SortableItem key={item.id} item={item} idx={idx} completed={completed} handleComplete={handleComplete} handleRemove={handleRemove} whiteCard />
                      ))
                    )}
                  </Box>
                </SortableContext>
              </DndContext>
            </CardContent>
          </Card>
          {/* Volunteer Plan Card */}
          <Card sx={{ flex: 1, minWidth: 0, maxWidth: 480, minHeight: 0, height: '100%', borderRadius: 6, boxShadow: '0 8px 32px 0 rgba(110,126,234,0.18)', background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)', color: '#fff', p: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', overflow: 'hidden' }}>
            <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
              <Typography variant="h5" fontWeight={900} sx={{ mb: 2, color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>🤝 Volunteering</Typography>
              <LinearProgress variant="determinate" value={volunteerProgress} sx={{ height: 12, borderRadius: 6, mb: 2, background: 'rgba(255,255,255,0.18)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ffe082 0%, #ff6b6b 100%)' } }} />
              <Typography variant="body2" sx={{ color: '#fff', mb: 2 }}>{volunteerCompleted}/{volunteerTotal} completed</Typography>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleVolunteerDragEnd}>
                <SortableContext items={volunteerItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
                  <Box sx={{ flex: 1, minHeight: 0, maxHeight: 420, overflowY: 'auto' }}>
                    {volunteerItems.length === 0 ? (
                      <Typography sx={{ color: '#fff', fontSize: 18, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>🌱 No volunteering yet — add some! 💡</Typography>
                    ) : (
                      volunteerItems.map((item, idx) => (
                        <SortableItem key={item.id} item={item} idx={idx} completed={completed} handleComplete={handleComplete} handleRemove={handleRemove} whiteCard />
                      ))
                    )}
                  </Box>
                </SortableContext>
              </DndContext>
            </CardContent>
          </Card>
        </Box>
        {/* Suggested Next Step */}
        <Box sx={{ width: '100%', maxWidth: 540, mb: 0, mt: 4, p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Card sx={{
            width: '100%',
            borderRadius: 6,
            boxShadow: '0 12px 48px 0 rgba(110,126,234,0.28)',
            background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
            color: '#fff',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            minHeight: 120,
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
              <RocketLaunchIcon sx={{ color: '#ffe082', fontSize: 44, mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ color: '#ffe082', fontWeight: 700, mb: 0.5 }}>
                  Suggested Next Step
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>
                  {aiLoading ? 'Thinking...' : aiNextStep.replace(/\|.*/, '')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(90deg, #ffe082 0%, #ff6b6b 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 3,
                  py: 0.8,
                  textTransform: 'none',
                  fontSize: 16,
                  boxShadow: '0 2px 8px #ffe08255',
                  '&:hover': { background: 'linear-gradient(90deg, #ff6b6b 0%, #ffe082 100%)', color: '#fff' }
                }}
                onClick={handleNextStep}
              >
                {aiNextStep && aiNextStep.startsWith('Finish:') ? 'Go to Task' : aiNextStep === 'Add your first extracurricular or find a volunteering opportunity!' ? 'Add Now' : 'Go to Dashboard'}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
      {/* Slide 3: Resume */}
      <Box sx={{ minHeight: 'calc(100dvh)', height: 'calc(100dvh)', width: '100%', scrollSnapAlign: 'start', bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', px: { xs: 2, md: 8 } }}>
        <Box sx={{ pt: { xs: 7, md: 8 }, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h3" fontWeight={900} gutterBottom sx={{ color: '#ffe082', mb: 2, mt: 0, textShadow: '0 2px 8px #6e7eea55', textAlign: 'center' }}>
            📄 Your AI-Powered Resume
          </Typography>
        </Box>
        {/* TODO: Pass completed activities to ResumeBuilder as a prop if supported */}
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ResumeBuilder activities={plan.filter(item => completed[item.id])} />
        </Box>
      </Box>
    </Box>
  );
} 